import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";

import default_theme from "assets/themes/themes";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import PrimaryOutlinedButton from "components/Common/PrimaryOutlinedButton";
import DangerButton from "components/Common/DangerButton";
import AddButton from "components/Common/AddButton";
import LocalTaskForceRequirements from "components/StepOnePage/LocalTaskForceRequirements";
import { saveProjectStakeholdersRequest } from "../../redux/projects/projectsSlice";

const LocalTaskForceSection = ({
  stakeholders,
  setStakeholders,
  localTaskForceMembers,
  rdds,
  roles,
  projectId,
  dispatch,
  onAddNewMember,
  setFormData,
  setCurrentIndex,
  setOpenModal,
}) => {
  const [openLtfDialog, setOpenLtfDialog] = useState(false);
  const [selectedStakeholderId, setSelectedStakeholderId] = useState("");

  const availableStakeholders = stakeholders.filter(
    (stakeholder) => !stakeholder.is_local_task_force_member
  );

  const getStakeholderName = (stakeholder) =>
    `${stakeholder?.first_name || ""} ${stakeholder?.last_name || ""}`.trim() ||
    "Unnamed stakeholder";

  const handleAddMember = () => {
    if (!selectedStakeholderId) {
      return;
    }

    const updated = stakeholders.map((stakeholder) =>
      String(stakeholder.id) === String(selectedStakeholderId)
        ? {
          ...stakeholder,
          is_local_task_force_member: true,
        }
        : stakeholder
    );

    setStakeholders(updated);

    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: updated,
      })
    );

    setOpenLtfDialog(false);
    setSelectedStakeholderId("");
  };

  const handleRemoveMember = (stakeholderId) => {
    const updated = stakeholders.map((stakeholder) =>
      stakeholder.id === stakeholderId
        ? {
          ...stakeholder,
          is_local_task_force_member: false,
          local_task_force_role_id: null,
        }
        : stakeholder
    );

    setStakeholders(updated);

    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: updated,
      })
    );
  };

  const handleRoleChange = (stakeholderId, value) => {
    const updated = stakeholders.map((stakeholder) =>
      stakeholder.id === stakeholderId
        ? {
          ...stakeholder,
          local_task_force_role_id: value || null,
        }
        : stakeholder
    );

    setStakeholders(updated);

    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: updated,
      })
    );
  };

  return (
    <Box sx={{ mt: 3 }}>

      <LocalTaskForceRequirements
        localTaskForceMembers={localTaskForceMembers}
      />

      <Box
        sx={{
          maxHeight: 350,
          overflowY: stakeholders.length > 3 ? "auto" : "visible",
          pr: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {localTaskForceMembers.length === 0 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                textAlign: "center",
                color: "#667085",
                border: "1px dashed #D0D5DD",
              }}
            >
              No Local Task Force members selected yet.
            </Paper>
          )}

          {localTaskForceMembers.map((stakeholder, index) => {
            const rdd = rdds.find((item) => item.id === stakeholder.rdd_id);

            return (
              <Paper
                key={stakeholder.id || index}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `2px solid ${default_theme.palette.other.figmaBlue}`,
                  backgroundColor: "#F5FAFD",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#23576B",
                      }}
                    >
                      {getStakeholderName(stakeholder)}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: 0.5,
                        alignItems: "center",
                      }}
                    >

                    {Boolean(stakeholder.gender?.trim()) && (
                        <Box
                          sx={{
                            background:
                              stakeholder.gender?.toLowerCase() === "female"
                                ? "linear-gradient(135deg, #C15C99, #D97BB0)"
                                : "linear-gradient(135deg, #23576B, #4F8FA8)",
                            color: "#fff",
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 5,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {stakeholder.gender}
                        </Box>
                      )}

                      {Boolean(stakeholder.grue?.trim()) && (
                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, #E53935, #FF6B6B)",
                            color: "#fff",
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 5,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          GRUE
                        </Box>
                      )}
                      </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {rdd && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {rdd.image_url && (
                            <Box
                              component="img"
                              src={rdd.image_url}
                              alt={rdd.name}
                              sx={{
                                width: 22,
                                height: 22,
                                objectFit: "contain",
                              }}
                            />
                          )}

                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              color: rdd.color || "#666",
                            }}
                          >
                            {rdd.name}
                          </Typography>
                        </Box>
                      )}

                      
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      select
                      label="LTF Role"
                      value={stakeholder.local_task_force_role_id || ""}
                      onChange={(e) =>
                        handleRoleChange(stakeholder.id, e.target.value)
                      }
                      sx={{
                        minWidth: 220,
                        ...blueTextFieldStyles(default_theme),
                      }}
                    >
                      <MenuItem value="">None</MenuItem>

                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <PrimaryOutlinedButton
                      onClick={() => {
                        const stakeholderIndex = stakeholders.findIndex(
                          (s) => s.id === stakeholder.id
                        );

                        setFormData({ ...stakeholder });
                        setCurrentIndex(stakeholderIndex);
                        setOpenModal(true);
                      }}
                    >
                      Edit
                    </PrimaryOutlinedButton>

                    <DangerButton
                      onClick={() => handleRemoveMember(stakeholder.id)}
                    >
                      Remove
                    </DangerButton>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <AddButton onClick={() => setOpenLtfDialog(true)}>
          + Add From Stakeholders
        </AddButton>

        <AddButton onClick={onAddNewMember}>
          + Create New Member
        </AddButton>
      </Box>

      <Dialog
        open={openLtfDialog}
        onClose={() => setOpenLtfDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Local Task Force Member</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <TextField
            select
            fullWidth
            label="Select Stakeholder"
            value={selectedStakeholderId}
            onChange={(e) => setSelectedStakeholderId(e.target.value)}
            sx={blueTextFieldStyles(default_theme)}
          >
            {availableStakeholders.map((stakeholder) => (
              <MenuItem key={stakeholder.id} value={stakeholder.id}>
                {getStakeholderName(stakeholder)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions>
          <DangerButton
            onClick={() => {
              setOpenLtfDialog(false);
              setSelectedStakeholderId("");
            }}
          >
            Cancel
          </DangerButton>

          <PrimaryOutlinedButton
            onClick={handleAddMember}
            disabled={!selectedStakeholderId}
          >
            Add Member
          </PrimaryOutlinedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocalTaskForceSection;
